import type { SchemaRule } from "@/types/schemaBuilder";
import { Lock } from "lucide-react";

interface SchemaRuleCardProps {
  rule: SchemaRule;
  compact?: boolean;
  isLocked?: boolean;
  onEdit?: (rule: SchemaRule) => void;
  onDelete?: (rule: SchemaRule) => void;
}

function getCategoryPillStyles(category: string) {
  switch (category.toLowerCase()) {
    case "arithmetic":
      return "border-green-300 text-green-600";
    case "syntactical":
      return "border-blue-300 text-blue-500";
    case "if-then":
      return "border-purple-300 text-purple-500";
    case "chronological":
      return "border-orange-300 text-orange-600";
    default:
      return "border-gray-300 text-gray-500";
  }
}

export function SchemaRuleCard({
  rule,
  compact = false,
  isLocked = false,
  onEdit,
  onDelete,
}: SchemaRuleCardProps) {
  return (
    <div
      className={`
        rounded-md
        border
        border-[var(--color-border-light)]
        border-l-4
        border-l-blue-600
        bg-white
        shadow-sm
        ${compact ? "p-3" : "p-4"}
      `}
    >
      <div className="mb-2 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{rule.title}</h3>

            <span
              className={`
                rounded-full
                border
                px-2
                py-0.5
                text-[10px]
                font-medium
                ${getCategoryPillStyles(rule.category)}
              `}
            >
              {rule.category}
            </span>

            {rule.hasConflict && (
              <span
                title={rule.conflictReason}
                className="rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-500"
              >
                Conflict
              </span>
            )}

            {isLocked && (
              <span className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                <Lock className="h-2.5 w-2.5" />
                Automatic
              </span>
            )}
          </div>

          <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{rule.ruleKey}</p>
        </div>

        <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
          {rule.fieldCount} field{rule.fieldCount !== 1 ? "s" : ""}
        </span>
      </div>

      {rule.formula && (
        <div className="mb-3 text-xs text-[var(--color-text-primary)]">{rule.formula}</div>
      )}

      {rule.description && rule.description !== rule.formula && (
        <div className="mb-4 text-xs text-[var(--color-text-secondary)]">{rule.description}</div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          disabled={isLocked}
          title={isLocked ? "Automatically created — cannot be edited" : undefined}
          onClick={() => onEdit?.(rule)}
          className="rounded border border-[var(--color-border)] px-4 py-1.5 text-xs text-[var(--color-text-primary)] transition hover:bg-[var(--color-bg-secondary)] disabled:cursor-not-allowed disabled:border-[var(--color-border-light)] disabled:text-[var(--color-text-tertiary)] disabled:hover:bg-transparent"
        >
          Edit Rule
        </button>

        <button
          type="button"
          disabled={isLocked}
          title={isLocked ? "Automatically created — cannot be deleted" : undefined}
          onClick={() => onDelete?.(rule)}
          className="rounded border border-red-300 px-4 py-1.5 text-xs text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-[var(--color-border-light)] disabled:text-[var(--color-text-tertiary)] disabled:hover:bg-transparent"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
